import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { CandlestickChart, Sun, Moon, LogOut, LayoutDashboard, Landmark, NotebookPen, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/app", label: "Workspace", icon: LayoutDashboard },
  { to: "/app/accounts", label: "Portfolios", icon: Landmark, match: ["/app/accounts", "/app/portfolio"] },
  { to: "/app/journal", label: "Journal", icon: NotebookPen },
];

function isActive(pathname, item) {
  if (item.to === "/app") return pathname === "/app";
  return (item.match || [item.to]).some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function Logo({ className }) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary">
        <CandlestickChart className="h-4 w-4 text-primary-foreground" />
      </span>
      <span className="font-display text-base font-semibold tracking-tight">My Trading Room</span>
    </span>
  );
}

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const initials = (user?.name || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const navItems = user?.role === "admin" ? [...NAV, { to: "/app/admin", label: "Admin", icon: ShieldCheck }] : NAV;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex h-14 items-center gap-4 px-3 sm:px-4 lg:px-6">
          <Link to="/app" data-testid="appshell-logo">
            <Logo />
          </Link>
          <nav className="ml-2 hidden items-center gap-1 sm:flex">
            {navItems.map((n) => {
              const active = isActive(location.pathname, n);
              const Icon = n.icon;
              return (
                <Link key={n.to} to={n.to}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-150",
                    active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  )}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                >
                  <Icon className="h-4 w-4" />
                  {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 rounded-full outline-none ring-ring focus-visible:ring-2" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture || undefined} alt={user?.name} />
                    <AvatarFallback className="bg-primary/15 text-xs font-semibold text-primary">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{user?.name}</div>
                    <div className="truncate text-xs font-normal text-muted-foreground">{user?.email}</div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <nav className="flex items-center gap-1 border-t px-3 py-1.5 sm:hidden">
          {navItems.map((n) => {
            const active = isActive(location.pathname, n);
            return (
              <Link key={n.to} to={n.to}
                className={cn("flex-1 rounded-lg px-2 py-1.5 text-center text-xs font-medium",
                  active ? "bg-secondary" : "text-muted-foreground")}>
                {n.label}
              </Link>
            );
          })}
        </nav>
      </header>
      <main className="flex min-h-0 flex-1 flex-col">{children}</main>
    </div>
  );
}
